import { ApiJob } from '../../core/jobs/entities/api-job.entity';
import { SortDirection } from '../../overviews/generic-overview/components/display-settings-bar/entities/sort-direction.entity';
import { sortByProperty } from '../../overviews/generic-overview/components/display-settings-bar/helpers/sort-by-property.helper';
import { ProcessingStatus, ProcessingStep } from './processing-step.entity';

/**
 * Generates a simplified processing progress overview for regular users
 * This is by no means a correct and complete representation of the processing steps in the backend
 */
export class ProcessingProgress {
    readonly #processingSteps: ProcessingStep[];
    readonly #jobs: ApiJob[];

    readonly #percentage: number;
    readonly #status: ProcessingStatus;

    constructor(processingSteps: ProcessingStep[], jobs: ApiJob[]) {
        this.#jobs = sortByProperty(jobs, 'CreationDate', SortDirection.ASC);
        this.#processingSteps = this.calculateProcessingSteps(processingSteps);
        this.#percentage = this.calculatePercentage();
        this.#status = this.calculateStatus();
    }

    get steps(): ProcessingStep[] {
        return this.#processingSteps;
    }

    get percentage(): number {
        return this.#percentage;
    }

    get status(): ProcessingStatus {
        return this.#status;
    }

    get labelClass(): string {
        return 'sh-progress-steps__step__label';
    }

    get statusClass(): string {
        switch (this.#status) {
            case ProcessingStatus.COMPLETED:
                return 'sh-progress-steps__step__status--success';

            default:
                return 'sh-progress-steps__step__status';
        }
    }

    private calculateProcessingSteps(steps: ProcessingStep[]): ProcessingStep[] {
        steps = this.setProcessingStepJobs(steps);
        steps = steps.sort((a, b) => this.sortProcessingSteps(a, b));

        return steps;
    }

    private setProcessingStepJobs(steps: ProcessingStep[]): ProcessingStep[] {
        steps.forEach(step => step.setJobs(this.#jobs));
        return steps;
    }

    private sortProcessingSteps(first: ProcessingStep, second: ProcessingStep): number {
        return first.creationDate >= second.creationDate ? 1 : -1;
    }

    private calculatePercentage(): number {
        const totalSteps: number = this.#processingSteps.filter(
            step => step.status !== ProcessingStatus.SKIPPED
        ).length;

        const nonFinishedStatus: ProcessingStatus[] = [
            ProcessingStatus.WAITING,
            ProcessingStatus.IN_PROGRESS,
            ProcessingStatus.SKIPPED
        ];
        const finishedSteps: number = this.#processingSteps.filter(
            step => !nonFinishedStatus.includes(step.status)
        ).length;

        const processingSteps: number = this.#processingSteps.filter(
            step => step.status === ProcessingStatus.IN_PROGRESS
        ).length;

        if (totalSteps === finishedSteps) {
            return 100;
        }

        const completedPercentage = (finishedSteps / totalSteps) * 100;
        const percentagePerStep = 100 / totalSteps;

        // Add half the percentage of completed steps per processing step
        const processingPercentage = processingSteps * (percentagePerStep / 2);

        return completedPercentage + processingPercentage;
    }

    private calculateStatus(): ProcessingStatus {
        if (this.hasFailedRequiredSteps()) {
            return ProcessingStatus.FAILED;
        }

        if (this.allRequiredStepsCompleted()) {
            return ProcessingStatus.COMPLETED;
        }

        return ProcessingStatus.IN_PROGRESS;
    }

    private requiredSteps(): ProcessingStep[] {
        return this.#processingSteps.filter(step => step.required);
    }

    private allRequiredStepsCompleted(): boolean {
        return this.requiredSteps().every(step => step.status === ProcessingStatus.COMPLETED);
    }

    private hasFailedRequiredSteps(): boolean {
        return this.requiredSteps().some(
            step => step.status === ProcessingStatus.FAILED || step.status === ProcessingStatus.INTERRUPTED
        );
    }
}
